import { storeOf } from "./sellers.js";
/**
 * Turning a Google Shopping response into something showable.
 *
 * Two jobs, and the second one is the important one.
 *
 * READING is defensive because the response is somebody else's JSON: every
 * field is optional as far as this code is concerned, and anything that cannot
 * be read as a price is dropped rather than defaulted.
 *
 * JUDGING is defensive because the data is genuinely dirty. The first live
 * call this feature ever made — `hajmola`, in Bengaluru — came back with forty
 * offers spanning ₹1 to ₹1,908, because Google Shopping matches on a phrase and
 * a phrase matches a single sweet, a 120-tablet bottle and a wholesale carton
 * equally well. "Cheapest: ₹1 at Fetch N Buy" is exactly the confidently wrong
 * number AGENTS.md rule 1 exists to prevent, so the junk is cut before anything
 * reaches a screen.
 */
/**
 * How far below the middle of the pack a price may sit before it is treated as
 * a different product rather than a bargain.
 *
 * A judgement call, and the honest way to describe it is: an offer at a third
 * of what everyone else is charging is almost never the same packet. Set
 * loosely enough that a genuine small pack survives — Zepto's ₹50 pouch against
 * a ₹65 median does — and tightly enough that the ₹1 listing does not.
 */
export const OUTLIER_FLOOR = 0.35;
/**
 * Below this there is no "middle of the pack" to compare against.
 *
 * With three offers a median is one number's opinion, and throwing away a third
 * of an already-thin answer is worse than showing it.
 */
const ENOUGH_TO_JUDGE = 4;
/** The most offers worth keeping. Past this they are all the same shops again. */
export const MAX_OFFERS = 40;
/**
 * Reads SerpApi's `shopping_results` into our own shape.
 *
 * `extracted_price` is a float in rupees and money here is integer paise
 * (AGENTS.md 7), so it is rounded once, at the boundary, and never carried as a
 * float again.
 */
export function readOffers(raw) {
    if (!Array.isArray(raw))
        return [];
    const out = [];
    for (const row of raw.slice(0, MAX_OFFERS)) {
        const r = (row ?? {});
        const seller = typeof r.source === "string" ? r.source.trim() : "";
        const rupees = typeof r.extracted_price === "number" ? r.extracted_price : Number.NaN;
        // A listing with no seller cannot be attributed and a listing with no
        // usable price is not a price. Both are dropped rather than guessed at.
        if (!seller || !Number.isFinite(rupees) || rupees <= 0)
            continue;
        const was = typeof r.extracted_old_price === "number" ? r.extracted_old_price : Number.NaN;
        out.push({
            seller,
            store: storeOf(seller),
            title: typeof r.title === "string" ? r.title.trim().slice(0, 120) : "",
            priceMinor: Math.round(rupees * 100),
            // Only when it really is a reduction. A merchant quoting an "old" price
            // at or below the new one is noise, and struck-through text that does not
            // mean a saving is worse than no struck-through text.
            oldPriceMinor: Number.isFinite(was) && was > rupees ? Math.round(was * 100) : null,
            delivery: typeof r.delivery === "string" && r.delivery ? r.delivery : null,
            thumbnail: https(r.thumbnail),
            rating: typeof r.rating === "number" && r.rating > 0 ? r.rating : null,
            // Paired with the rating on purpose: "261 reviews" beside no score says
            // nothing, and a score with no count invites more trust than it earns.
            reviews: typeof r.rating === "number" && typeof r.reviews === "number" ? r.reviews : null,
            link: https(r.product_link),
        });
    }
    return out;
}
/**
 * Drops the listings that are too cheap to be the same product.
 *
 * Returns the input untouched when there is not enough of it to have an
 * opinion — see `ENOUGH_TO_JUDGE`.
 */
export function sane(offers) {
    if (offers.length < ENOUGH_TO_JUDGE)
        return [...offers];
    const floor = median(offers.map((o) => o.priceMinor)) * OUTLIER_FLOOR;
    return offers.filter((o) => o.priceMinor >= floor);
}
/** Only ever an https URL. Anything else is dropped rather than rendered. */
function https(value) {
    return typeof value === "string" && value.startsWith("https://") ? value : null;
}
/** The middle value. Even counts take the lower of the two, which is fine here. */
function median(values) {
    const sorted = [...values].sort((a, b) => a - b);
    return sorted[Math.floor((sorted.length - 1) / 2)] ?? 0;
}
/**
 * What each shop's listings span.
 *
 * A RANGE and not one number, because one number is a claim this data cannot
 * support: a shop's offers for "milk" run from a 200ml carton to a six-pack,
 * and quoting the lowest as "the price at Zepto" invites someone to tap
 * expecting it. "₹20–₹77" is the honest shape of the answer, and it is also the
 * more useful one — a wide span says "this shop stocks several sizes", which is
 * a reason to go and look.
 *
 * Collapses to a single value when a shop quoted one price, or the same price
 * twice; the caller renders `low` alone in that case.
 */
export function priceRanges(offers) {
    const spans = new Map();
    for (const o of offers) {
        if (!o.store)
            continue;
        const held = spans.get(o.store);
        if (!held) {
            spans.set(o.store, { low: o.priceMinor, high: o.priceMinor });
            continue;
        }
        if (o.priceMinor < held.low)
            held.low = o.priceMinor;
        if (o.priceMinor > held.high)
            held.high = o.priceMinor;
    }
    return spans;
}
//# sourceMappingURL=offers.js.map